import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Auth.css';
import { API_URL } from './config/apiConfig';
import { isCapacitorNative, buildCapacitorGoogleAuthUrl, buildWebOrFileGoogleAuthUrl } from './mobile/oauthDeepLink';
import { useContainerScale } from './hooks/useViewportScale';

function Register({ setUser }) {
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('register.errors.nameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('register.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('register.errors.emailInvalid');
    }

    if (!formData.username.trim()) {
      newErrors.username = t('register.errors.usernameRequired');
    } else if (formData.username.length < 3) {
      newErrors.username = t('register.errors.usernameMinLength');
    }

    if (!formData.password) {
      newErrors.password = t('register.errors.passwordRequired');
    } else if (formData.password.length < 8) {
      newErrors.password = t('register.errors.passwordMinLength');
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/.test(formData.password)) {
      newErrors.password = t('register.errors.passwordComplexity');
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('register.errors.passwordMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const apiUrl = API_URL;
      const response = await fetch(`${apiUrl}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          surname: formData.surname.trim(),
          email: formData.email.trim(),
          username: formData.username.trim(),
          password: formData.password
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('register.errors.registrationFailed'));
      }

      setSuccess(true);
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    console.log('🔵 handleGoogleSignIn called (Register)');
    console.log('   Protocol:', window.location.protocol);
    console.log('   window.electron:', window.electron !== undefined ? 'exists' : 'undefined');
    console.log('   API_URL:', API_URL);
    
    // Check if we're in Electron
    const isInElectron = window.electron !== undefined && window.electron.openOAuthWindow;
    
    if (isInElectron) {
      // Electron: Open OAuth in a separate popup window
      // This keeps the main window on file:// protocol so images load correctly
      console.log('🔵 Opening OAuth in popup window (Electron mode)');
      const authUrl = `${API_URL}/auth/google`;
      window.electron.openOAuthWindow(authUrl);
      
      // Listen for OAuth success - will redirect to login/welcome page
      window.electron.onOAuthSuccess((data) => {
        console.log('✅ OAuth success received:', data.username);
        
        // Store tokens in localStorage
        localStorage.setItem('token', data.token);
        localStorage.setItem('userId', data.userId);
        localStorage.setItem('user', data.username);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        
        // Clean up listener
        window.electron.removeOAuthListener();
        
        // Redirect to welcome page
        window.location.href = window.location.origin + window.location.pathname + '#/post-login-video';
        window.location.reload();
      });
    } else if (isCapacitorNative()) {
      const targetUrl = buildCapacitorGoogleAuthUrl();
      console.log('🔵 Navigating to (Capacitor native):', targetUrl);
      window.location.href = targetUrl;
    } else {
      const targetUrl = buildWebOrFileGoogleAuthUrl();
      console.log('🔵 Navigating to (Web mode):', targetUrl);
      window.location.href = targetUrl;
    }
  };

  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();

  if (success) {
    return (
      <div className="auth-viewport-wrapper">
        <div className="auth-fullscreen-background" />
        <div 
          className="auth-viewport-container"
          style={{
            width: `${containerBaseWidth}px`,
            height: `${containerBaseHeight}px`,
            transformOrigin: '0 0',
            transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
          }}
        >
          <div className={`auth-container ${isContainerMobile ? 'auth-mobile' : ''}`}>
            <div className="auth-card">
              <div className="auth-game-title">{t('welcome.gameTitle')}</div>
              <div className="auth-message" style={{ marginTop: 20 }}>
                <p>{t('register.success.message')}</p>
                <p>{t('register.success.checkEmail')}</p>
              </div>
              <div className="auth-links" style={{ marginTop: 20, flexDirection: 'row', justifyContent: 'center', gap: 15 }}>
                <Link to="/resend-confirmation" className="auth-link">
                  {t('register.success.resendEmail')}
                </Link>
                <Link to="/" className="auth-link">
                  {t('register.success.backToLogin')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-viewport-wrapper">
      <div className="auth-fullscreen-background" />
      <div 
        className="auth-viewport-container"
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className={`auth-container ${isContainerMobile ? 'auth-mobile' : ''}`}>
          <div className="auth-card">
            <div className="auth-game-title">{t('welcome.gameTitle')}</div>
            
            <form className="auth-form" onSubmit={handleSubmit}>
              {/* Name and Surname in a row on mobile */}
              <div className={isContainerMobile ? "auth-form-row" : ""}>
                <div className="auth-input-group">
                  <input
                    type="text"
                    name="name"
                    className="auth-input"
                    placeholder={t('register.name')}
                    value={formData.name}
                    onChange={handleChange}
                  />
                  {errors.name && <div className="field-error">{errors.name}</div>}
                </div>

                <div className="auth-input-group">
                  <input
                    type="text"
                    name="surname"
                    className="auth-input"
                    placeholder={t('register.surname')}
                    value={formData.surname}
                    onChange={handleChange}
                  />
                  {errors.surname && <div className="field-error">{errors.surname}</div>}
                </div>
              </div>

              {/* Email and Username in a row on mobile */}
              <div className={isContainerMobile ? "auth-form-row" : ""}>
                <div className="auth-input-group">
                  <input
                    type="email"
                    name="email"
                    className="auth-input"
                    placeholder={t('register.email')}
                    value={formData.email}
                    onChange={handleChange}
                  />
                  {errors.email && <div className="field-error">{errors.email}</div>}
                </div>

                <div className="auth-input-group">
                  <input
                    type="text"
                    name="username"
                    className="auth-input"
                    placeholder={t('register.username')}
                    value={formData.username}
                    onChange={handleChange}
                  />
                  {errors.username && <div className="field-error">{errors.username}</div>}
                </div>
              </div>

              {/* Password fields in a row on mobile */}
              <div className={isContainerMobile ? "auth-form-row" : ""}>
                <div className="auth-input-group">
                  <input
                    type="password"
                    name="password"
                    className="auth-input"
                    placeholder={t('register.password')}
                    value={formData.password}
                    onChange={handleChange}
                  />
                  {errors.password && <div className="field-error">{errors.password}</div>}
                </div>

                <div className="auth-input-group">
                  <input
                    type="password"
                    name="confirmPassword"
                    className="auth-input"
                    placeholder={t('register.confirmPassword')}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                  {errors.confirmPassword && <div className="field-error">{errors.confirmPassword}</div>}
                </div>
              </div>

              {errors.submit && (
                <div className="auth-error">{errors.submit}</div>
              )}

              <button 
                type="submit" 
                className="auth-button"
                disabled={loading}
              >
                {loading ? t('register.loading') : t('register.submit')}
              </button>
            </form>

            <div className="auth-divider">
              <span>{t('register.or')}</span>
            </div>

            <button
              onClick={handleGoogleSignIn}
              className="auth-button-google"
            >
              <svg className="google-logo" width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <g fill="#000" fillRule="evenodd">
                  <path d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z" fill="#EA4335"/>
                  <path d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.21 1.18-.84 2.08-1.79 2.68l2.78 2.16c2.01-1.86 3.17-4.6 3.17-7.34z" fill="#4285F4"/>
                  <path d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9.008 9.008 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z" fill="#FBBC05"/>
                  <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.78-2.16c-.76.53-1.78.9-3.18.9-2.38 0-4.4-1.57-5.12-3.74L.96 13.04C2.45 15.98 5.48 18 9 18z" fill="#34A853"/>
                </g>
              </svg>
              {t('register.googleSignIn')}
            </button>

            <div className="auth-links">
              <Link to="/" className="auth-link">{t('register.alreadyHaveAccount')}</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;

